/**
 * WrittenReportTab - Best-in-class linear document view
 * 
 * Features:
 * - Structured TOC with elegant design
 * - Clean reading modes
 * - Inline citations without scroll jumping
 * - Copy-block functionality
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { WrittenReportView, CIG, ReportSection } from '../../../lib/cockpit/schemas'
import OutboundLink from '../../shared/OutboundLink'
import { colorizeNumericContent } from '../../../lib/colorizeNumericText'

interface WrittenReportTabProps {
  view: WrittenReportView
  cig: CIG
  onOpenEvidence?: (blockId: string) => void
  viewMode?: 'mobile' | 'tablet' | 'desktop'
}

function isTimelineReportSection(section?: ReportSection): boolean {
  if (!section) return false

  const sectionId = (section.id || '').toLowerCase()
  const title = (section.title || '').toLowerCase()
  if (sectionId === 'timeline' || sectionId.includes('milestone')) {
    return true
  }

  return ['timeline', 'milestone', 'chronology', 'major events', 'key dates'].some((keyword) =>
    title.includes(keyword)
  )
}

function WrittenReportTab({ view, cig, onOpenEvidence, viewMode = 'desktop' }: WrittenReportTabProps) {
  const [activeTocItem, setActiveTocItem] = useState<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const tocNavRef = useRef<HTMLDivElement>(null)
  const tocItemRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  // Track if initial scroll spy has run
  const initializedRef = useRef(false)

  const isMobile = viewMode === 'mobile'
  const deepResearchMarkdown = view.deep_research_markdown?.trim()
  const hasDeepResearch = Boolean(deepResearchMarkdown)
  
  // Extract headings with stable IDs using a deterministic algorithm
  const markdownHeadings = useMemo(
    () => (hasDeepResearch && deepResearchMarkdown ? extractMarkdownHeadings(deepResearchMarkdown) : []),
    [deepResearchMarkdown, hasDeepResearch]
  )
  const timelineSections = useMemo(
    () => view.sections.filter((section) => isTimelineReportSection(section)),
    [view.sections]
  )
  
  // Create a lookup map from heading text to ID - this is order-independent and works with StrictMode
  const headingTextToId = useMemo(() => {
    const map = new Map<string, string>()
    const textCounts = new Map<string, number>()
    
    markdownHeadings.forEach(h => {
      const normalizedText = h.title.toLowerCase().trim()
      const count = textCounts.get(normalizedText) || 0
      // For first occurrence, use the text as key; for duplicates, add index
      const key = count === 0 ? normalizedText : `${normalizedText}__${count}`
      map.set(key, h.id)
      textCounts.set(normalizedText, count + 1)
    })
    
    return map
  }, [markdownHeadings])
  
  // Track which texts we've seen during this render cycle (for handling duplicates)
  const seenTextsRef = useRef(new Map<string, number>())
  
  const tocItems = hasDeepResearch
    ? [...markdownHeadings, ...generateTocItems(timelineSections)]
    : generateTocItems(view.sections)
  
  // Reset seen texts before each render cycle
  seenTextsRef.current = new Map()

  // Auto-scroll TOC to keep active item visible
  useEffect(() => {
    if (!activeTocItem || !tocNavRef.current) return
    const activeButton = tocItemRefs.current.get(activeTocItem)
    if (activeButton) {
      const nav = tocNavRef.current
      const buttonRect = activeButton.getBoundingClientRect()
      const navRect = nav.getBoundingClientRect()
      
      // Check if button is outside visible area of nav
      if (buttonRect.top < navRect.top || buttonRect.bottom > navRect.bottom) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [activeTocItem])

  const renderMarkdownHeading = useCallback((level: number) => ({
    children,
  }: {
    children: React.ReactNode[]
  }) => {
    const plainText = getPlainText(children)
    const normalizedText = plainText.toLowerCase().trim()
    
    // Track how many times we've seen this text to handle duplicates
    const seenCount = seenTextsRef.current.get(normalizedText) || 0
    seenTextsRef.current.set(normalizedText, seenCount + 1)
    
    // Look up the ID from our pre-computed map
    // For first occurrence, use plain text; for duplicates, add the index
    const lookupKey = seenCount === 0 ? normalizedText : `${normalizedText}__${seenCount}`
    const id = headingTextToId.get(lookupKey) || slugifyRawHeading(plainText)
    
    const HeadingTag = `h${Math.min(level + 1, 6)}` as keyof JSX.IntrinsicElements
    const levelClasses =
      level <= 1
        ? 'text-3xl md:text-4xl font-bold'
        : level === 2
          ? 'text-2xl font-bold'
          : 'text-xl font-semibold'

    return (
      <HeadingTag
        id={id}
        data-report-section={id}
        className={`${levelClasses} text-gray-900 dark:text-white mt-10 mb-4 scroll-mt-28`}
      >
        {children}
      </HeadingTag>
    )
  }, [headingTextToId])

  const markdownComponents: Record<string, React.ElementType> = {
    h1: renderMarkdownHeading(1),
    h2: renderMarkdownHeading(2),
    h3: renderMarkdownHeading(3),
    h4: renderMarkdownHeading(3),
    p: ({ children }: { children: React.ReactNode[] }) => (
      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{children}</p>
    ),
    a: ({ href, children }: { href?: string; children: React.ReactNode[] }) => {
      const text = String(children ?? '')
      const isUrl = /^(https?:\/\/|www\.)/.test(text) || (/\.\w{2,}/.test(text) && !text.includes(' '))
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 text-blue-600 dark:text-blue-400 hover:text-blue-500"
          title={href}
        >
          {!isUrl && children}
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="none">
            <path d="M6 14l8-8m0 0h-5m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      )
    },
    ul: ({ children }: { children: React.ReactNode[] }) => (
      <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode[] }) => (
      <ol className="list-decimal pl-6 space-y-2 text-gray-700 dark:text-gray-300">{children}</ol>
    ),
    li: ({ children }: { children: React.ReactNode[] }) => <li className="ml-1">{children}</li>,
    blockquote: ({ children }: { children: React.ReactNode[] }) => (
      <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400">
        {children}
      </blockquote>
    ),
    pre: ({ children }: { children: React.ReactNode[] }) => (
      <pre className="overflow-x-auto bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-sm">{children}</pre>
    ),
    code: ({ children }: { children: React.ReactNode[] }) => (
      <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm break-all">{children}</code>
    ),
    table: ({ children }: { children: React.ReactNode[] }) => (
      <div className="overflow-x-auto -mx-4 px-4 my-4">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode[] }) => (
      <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
    ),
    th: ({ children }: { children: React.ReactNode[] }) => (
      <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">{children}</th>
    ),
    td: ({ children }: { children: React.ReactNode[] }) => (
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800">{children}</td>
    ),
    // Disable strikethrough - tildes (~) in research content often mean "approximately" not strikethrough
    del: ({ children }: { children: React.ReactNode[] }) => <>{children}</>,
    s: ({ children }: { children: React.ReactNode[] }) => <>{children}</>,
  }

  // Handle citation click
  const handleCitationClick = useCallback((sourceId: string) => {
    if (onOpenEvidence) {
      onOpenEvidence(sourceId)
    }
  }, [onOpenEvidence])

  const handleTocClick = useCallback((sectionId: string) => {
    const allDocSections = document.querySelectorAll('[data-report-section]')
    
    // Try multiple strategies to find the element
    let el: HTMLElement | null = null
    
    // Strategy 1: Direct ID match
    el = document.getElementById(sectionId)
    
    // Strategy 2: Data attribute match
    if (!el) {
      el = document.querySelector(`[data-report-section="${sectionId}"]`) as HTMLElement
    }
    
    // Strategy 3: Case-insensitive match
    if (!el) {
      for (const section of allDocSections) {
        const attr = section.getAttribute('data-report-section')
        if (attr && attr.toLowerCase() === sectionId.toLowerCase()) {
          el = section as HTMLElement
          break
        }
      }
    }
    
    // Strategy 4: Slug similarity
    if (!el) {
      const targetSlug = sectionId.toLowerCase().replace(/[^a-z0-9]/g, '')
      for (const section of allDocSections) {
        const attr = section.getAttribute('data-report-section') || ''
        if (attr.toLowerCase().replace(/[^a-z0-9]/g, '') === targetSlug) {
          el = section as HTMLElement
          break
        }
      }
    }
    
    if (el) {
      // Find the scrollable parent container (might not be window)
      const findScrollableParent = (element: HTMLElement | null): HTMLElement | Window => {
        if (!element) return window
        let current: HTMLElement | null = element.parentElement
        while (current) {
          const { overflow, overflowY } = window.getComputedStyle(current)
          const isScrollable = overflow === 'auto' || overflow === 'scroll' || 
                               overflowY === 'auto' || overflowY === 'scroll'
          if (isScrollable && current.scrollHeight > current.clientHeight) {
            return current
          }
          current = current.parentElement
        }
        return window
      }
      
      const scrollContainer = findScrollableParent(el)
      const headerOffset = 120 // Offset for sticky header
      
      if (scrollContainer === window) {
        // Use window scroll
        const rect = el.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const targetPosition = rect.top + scrollTop - headerOffset
        
        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'smooth'
        })
      } else {
        // Use container scroll
        const container = scrollContainer as HTMLElement
        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        const targetPosition = container.scrollTop + (elRect.top - containerRect.top) - headerOffset
        
        container.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'smooth'
        })
      }
      
      setActiveTocItem(sectionId)
    }
  }, [])

  // Scroll spy: Updates the active TOC item based on which section is visible
  // Uses both IntersectionObserver for efficiency and scroll events for reliability
  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    // Store observer reference for cleanup
    let observer: IntersectionObserver | null = null
    let scrollHandler: (() => void) | null = null
    // Track the current active item without needing it as a dependency
    let currentActiveId: string | null = null
    
    // Helper to find the section closest to the top of the viewport
    const updateActiveSection = () => {
      const sectionElements = Array.from(container.querySelectorAll('[data-report-section]')) as HTMLElement[]
      if (!sectionElements.length) return
      
      let bestId: string | null = null
      let bestDistance = Infinity
      let foundPassedSection = false
      const headerOffset = 120 // Account for sticky header
      
      sectionElements.forEach(el => {
        const id = el.getAttribute('data-report-section')
        if (!id) return
        
        const rect = el.getBoundingClientRect()
        const distanceFromIdealPosition = rect.top - headerOffset
        
        if (distanceFromIdealPosition <= 0) {
          // Section has scrolled past the header - prioritize these
          if (!foundPassedSection) {
            // First passed section found - reset to prioritize passed sections over upcoming ones
            foundPassedSection = true
            bestDistance = Infinity
          }
          const distance = Math.abs(distanceFromIdealPosition)
          if (distance < bestDistance) {
            bestDistance = distance
            bestId = id
          }
        } else if (!foundPassedSection) {
          // No section has passed yet - find the closest upcoming section
          if (distanceFromIdealPosition < bestDistance) {
            bestDistance = distanceFromIdealPosition
            bestId = id
          }
        }
        // If a section has passed, ignore sections that haven't passed
      })
      
      if (bestId && bestId !== currentActiveId) {
        currentActiveId = bestId
        setActiveTocItem(bestId)
      }
    }
    
    // Small delay to ensure DOM is ready after markdown rendering
    const timeoutId = setTimeout(() => {
      const sectionElements = Array.from(container.querySelectorAll('[data-report-section]')) as HTMLElement[]
      if (!sectionElements.length) return

      // Set first section as active by default (only once)
      if (!initializedRef.current) {
        const firstId = sectionElements[0]?.getAttribute('data-report-section')
        if (firstId) {
          currentActiveId = firstId
          setActiveTocItem(firstId)
          initializedRef.current = true
        }
      }

      // Use IntersectionObserver for efficient updates
      observer = new IntersectionObserver(
        () => {
          // When any section enters/exits, recalculate the best section
          updateActiveSection()
        },
        { 
          // Observe sections relative to viewport with generous margins
          rootMargin: '-100px 0px -40% 0px', 
          threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] 
        },
      )

      sectionElements.forEach(el => observer!.observe(el))
      
      // Also add scroll listener for more reliable updates
      // Use throttling for performance
      let ticking = false
      scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            updateActiveSection()
            ticking = false
          })
          ticking = true
        }
      }
      
      window.addEventListener('scroll', scrollHandler, { passive: true })
      
      // Initial calculation
      updateActiveSection()
    }, 100)
    
    // Cleanup: clear timeout, disconnect observer, remove scroll listener
    return () => {
      clearTimeout(timeoutId)
      if (observer) {
        observer.disconnect()
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler)
      }
    }
  }, [view.sections, hasDeepResearch, deepResearchMarkdown])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="flex max-w-[1600px] mx-auto">
        {/* Sticky TOC (desktop) - Elegant card design */}
        {!isMobile && view.toc_enabled && (
          <aside className="hidden lg:block w-72 flex-shrink-0 p-4 relative z-10 min-w-0">
            <nav 
              ref={tocNavRef}
              className="sticky top-0 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 z-20 pt-4"
            >
              {/* TOC Card Container */}
              <div className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700/50 shadow-sm overflow-hidden backdrop-blur-sm">
                {/* Header */}
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700/50">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Contents
                    </h3>
                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 font-medium">
                      {tocItems.length}
                    </span>
                  </div>
                </div>
                
                {/* TOC Items */}
                <div className="py-2 px-2">
                  {tocItems.map((item, index) => {
                    const isActive = activeTocItem === item.id
                    const isTopLevel = item.level === 1
                    const isSubsection = item.level > 1
                    
                    // Get section number for top-level items
                    const sectionNumber = isTopLevel 
                      ? tocItems.filter((t, i) => i <= index && t.level === 1).length
                      : null
                    
                    return (
                      <button
                        key={item.id}
                        ref={(el) => {
                          if (el) tocItemRefs.current.set(item.id, el)
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleTocClick(item.id)
                        }}
                        type="button"
                        className={`
                          group relative flex items-start gap-2.5 w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-200
                          ${isSubsection ? 'ml-4' : ''}
                          ${isActive
                            ? 'bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-200 dark:ring-blue-800'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }
                        `}
                      >
                        {/* Section number indicator for top-level */}
                        {isTopLevel && sectionNumber && (
                          <span className={`
                            flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold transition-colors
                            ${isActive 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600'
                            }
                          `}>
                            {sectionNumber}
                          </span>
                        )}
                        
                        {/* Subsection indicator */}
                        {isSubsection && (
                          <span className={`
                            flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2 transition-colors
                            ${isActive 
                              ? 'bg-blue-500' 
                              : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-gray-400 dark:group-hover:bg-gray-500'
                            }
                          `} />
                        )}
                        
                        {/* Title */}
                        <span className={`
                          flex-1 leading-snug transition-colors
                          ${isActive 
                            ? 'text-blue-700 dark:text-blue-300 font-semibold' 
                            : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'
                          }
                          ${isTopLevel ? 'font-medium' : 'text-[13px]'}
                          line-clamp-2
                        `}>
                          {item.title}
                        </span>
                        
                        {/* Active indicator arrow */}
                        {isActive && (
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </nav>
          </aside>
        )}

        {/* Main content */}
        <article ref={contentRef} className="flex-1 min-w-0 max-w-5xl px-4 md:px-8 py-8 space-y-6 break-words overflow-x-hidden">
          {/* Title */}
          <header className="mb-4">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
              {view.title}
            </h1>
            {view.subtitle && (
              <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
                {view.subtitle}
              </p>
            )}
            {!hasDeepResearch && (
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                {view.author && (
                  <span>By {view.author.name}</span>
                )}
                <span>•</span>
                <span>Updated {formatDate(view.last_updated)}</span>
                <span>•</span>
                <span className="capitalize">{view.confidence} confidence</span>
              </div>
            )}
          </header>

          {!hasDeepResearch && (
            <>
              {/* BLUF */}
              <section className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border-l-4 border-blue-500">
                <h2 className="text-sm font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2">
                  Bottom Line
                </h2>
                <p className="text-lg font-medium text-gray-900 dark:text-white leading-relaxed">
                  {view.bluf}
                </p>
              </section>

              {/* Executive Summary */}
              <section className="mb-8">
                <h2 id="executive-summary" className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Executive Summary
                </h2>
                <ul className="space-y-2">
                  {view.executive_summary.map((item: any, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        <MarkdownInline text={item.text} />
                        {item.citation_refs && item.citation_refs.length > 0 && (
                          <CitationRefs refs={item.citation_refs} onClick={handleCitationClick} />
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Figure Gallery Preview */}
              {view.figure_gallery_enabled && view.figures && view.figures.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Key Figures
                  </h2>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {view.figures.slice(0, 4).map((fig: any) => (
                      <div
                        key={fig.id}
                        className="flex-shrink-0 w-40 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Figure {fig.number}
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                          {fig.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Main Body Sections */}
          {!hasDeepResearch && view.sections.map((section: any, index: number) => (
            <ReportSectionComponent
              key={section.id || `section-${index}`}
              section={section}
              onCitationClick={handleCitationClick}
            />
          ))}

          {hasDeepResearch && deepResearchMarkdown && (
            <>
              <section className="my-8 space-y-6">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {deepResearchMarkdown}
                </ReactMarkdown>
              </section>
              {timelineSections.map((section: any, index: number) => (
                <ReportSectionComponent
                  key={section.id || `timeline-section-${index}`}
                  section={section}
                  onCitationClick={handleCitationClick}
                />
              ))}
            </>
          )}

          {/* Counterpoints */}
          {view.counterpoints && view.counterpoints.length > 0 && (
            <section className="my-8 p-6 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Counterpoints & Uncertainty
              </h2>
              <div className="space-y-4">
                {view.counterpoints.map((cp: any, index: number) => (
                  <div key={index} className="border-l-2 border-orange-300 dark:border-orange-700 pl-4">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {cp.claim}
                    </p>
                    <p className="mt-1 text-gray-600 dark:text-gray-400 italic">
                      Counterpoint: {cp.counterpoint}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Implications */}
          {view.implications && view.implications.length > 0 && (
            <section className="my-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Implications
              </h2>
              {view.implications.map((imp: any, index: number) => (
                <div key={index} className="mb-4">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">
                    For {imp.audience}
                  </h3>
                  <ul className="space-y-1">
                    {imp.items.map((item: any, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                        <span className="text-green-500">→</span>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )}

          {/* Methodology */}
          {view.methodology && (
            <section className="my-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Methodology
              </h2>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                {view.methodology}
              </p>
            </section>
          )}

          {/* Limitations */}
          {view.limitations && view.limitations.length > 0 && (
            <section className="my-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Limitations
              </h2>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {view.limitations.map((lim: string, index: number) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-yellow-500">⚠</span>
                    {lim}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Footnotes */}
          {view.footnotes.length > 0 && (
            <section className="my-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Sources
              </h2>
              <ol className="space-y-2 text-sm">
                {view.footnotes.map((fn: any) => (
                  <li key={fn.id} className="flex gap-2 text-gray-600 dark:text-gray-400">
                    <span className="font-mono text-blue-600 dark:text-blue-400">
                      [{fn.number}]
                    </span>
                    <span>{fn.text}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Fallback markdown - render with ReactMarkdown so [text](url) links are clickable */}
          {view.fallback_markdown && !view.sections.length && (
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {view.fallback_markdown}
              </ReactMarkdown>
            </div>
          )}
        </article>
      </div>
    </div>
  )
}

// Helper components
function ReportSectionComponent({
  section,
  onCitationClick,
  depth = 1,
}: {
  section: ReportSection
  onCitationClick: (id: string) => void
  depth?: number
}) {
  const HeadingTag = `h${Math.min(depth + 1, 6)}` as keyof JSX.IntrinsicElements
  
  return (
    <section
      id={section.id}
      data-report-section={section.id}
      className="my-8 scroll-mt-24"
    >
      <HeadingTag className={`font-bold text-gray-900 dark:text-white mb-3 ${
        depth === 1 ? 'text-xl' : depth === 2 ? 'text-lg' : 'text-base'
      }`}>
        {section.title}
      </HeadingTag>
      
      {section.one_line_summary && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-3">
          In one line: {section.one_line_summary}
        </p>
      )}
      
      <div className="space-y-4">
        {section.content_blocks.map((block: any, index: number) => (
          <ContentBlock
            key={`${section.id}-${index}`}
            block={block}
            onCitationClick={onCitationClick}
          />
        ))}
      </div>
      
      {section.subsections?.map((sub: any, index: number) => (
        <ReportSectionComponent
          key={sub.id || `${section.id}-sub-${index}`}
          section={sub}
          onCitationClick={onCitationClick}
          depth={depth + 1}
        />
      ))}
    </section>
  )
}

function ContentBlock({
  block,
  onCitationClick,
}: {
  block: ReportSection['content_blocks'][0]
  onCitationClick: (id: string) => void
}) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          <MarkdownInline text={block.content} />
          {block.citation_refs && (
            <CitationRefs refs={block.citation_refs} onClick={onCitationClick} />
          )}
        </p>
      )
    
    case 'heading':
      const Tag = `h${block.level || 3}` as keyof JSX.IntrinsicElements
      return (
        <Tag className="font-semibold text-gray-900 dark:text-white">
          {block.content}
        </Tag>
      )
    
    case 'list':
      return (
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          {block.items?.map((item: string, i: number) => (
            <li key={i}>
              <MarkdownInline text={item} />
            </li>
          ))}
        </ul>
      )
    
    case 'quote':
      return (
        <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400">
          <MarkdownInline text={block.content} />
        </blockquote>
      )
    
    case 'callout':
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-800 dark:text-yellow-200">
          <MarkdownInline text={block.content} />
        </div>
      )
    
    default:
      return <p className="text-gray-700 dark:text-gray-300"><MarkdownInline text={block.content} /></p>
  }
}

function CitationRefs({ refs, onClick }: { refs: string[]; onClick: (id: string) => void }) {
  return (
    <span className="inline-flex gap-0.5 ml-1">
      {refs.map((ref, i) => (
        <button
          key={ref}
          onClick={() => onClick(ref)}
          className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-mono"
        >
          [{i + 1}]
        </button>
      ))}
    </span>
  )
}

function MarkdownInline({ text }: { text: string }) {
  if (!text) return null
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ children, href }) => {
          const text = String(children ?? '')
          const isUrl = /^(https?:\/\/|www\.)/.test(text) || (/\.\w{2,}/.test(text) && !text.includes(' '))
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-blue-600 dark:text-blue-400 hover:text-blue-500"
              title={href}
            >
              {!isUrl && children}
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                <path d="M6 14l8-8m0 0h-5m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )
        },
        p: ({ children }) => <>{colorizeNumericContent(children)}</>,
        strong: ({ children }) => <strong className="font-semibold">{colorizeNumericContent(children)}</strong>,
        em: ({ children }) => <em className="italic">{colorizeNumericContent(children)}</em>,
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

function extractMarkdownHeadings(markdown: string): { id: string; title: string; level: number }[] {
  const lines = markdown.split(/\r?\n/)
  const slugCounts: Record<string, number> = {}
  const headings: { id: string; title: string; level: number }[] = []
  
  // Track if we've seen any real headings (# format)
  let hasRealHeadings = false

  lines.forEach(line => {
    // First, try to match standard markdown headings
    const match = line.match(/^(#{1,6})\s+(.*)$/)
    if (match) {
      hasRealHeadings = true
      const level = match[1].length
      const title = match[2].trim()
      if (!title) return
      const base = slugifyRawHeading(title)
      const count = slugCounts[base] ?? 0
      slugCounts[base] = count + 1
      const id = count > 0 ? `${base}-${count}` : base
      headings.push({ id, title, level })
    }
  })
  
  // If we found very few headings (1 or less), also extract bold section headers
  // This handles reports that use **Section Name:** or **Section Name** format
  if (headings.length <= 1) {
    const boldSectionPattern = /^\*\*([A-Z][^*:]+?)(?::\*\*|\*\*:|\*\*)/gm
    let boldMatch
    const seenBoldTitles = new Set(headings.map(h => h.title.toLowerCase()))
    
    while ((boldMatch = boldSectionPattern.exec(markdown)) !== null) {
      const title = boldMatch[1].trim()
      // Skip if it's too short or looks like inline emphasis
      if (title.length < 4 || title.length > 80) continue
      // Skip if already added as a heading
      if (seenBoldTitles.has(title.toLowerCase())) continue
      // Skip common inline phrases that aren't section headers
      const skipPhrases = ['note', 'important', 'warning', 'example', 'source', 'citation']
      if (skipPhrases.some(p => title.toLowerCase().startsWith(p))) continue
      
      const base = slugifyRawHeading(title)
      const count = slugCounts[base] ?? 0
      slugCounts[base] = count + 1
      const id = count > 0 ? `${base}-${count}` : base
      // Add bold sections as level 2 (## equivalent)
      headings.push({ id, title, level: 2 })
      seenBoldTitles.add(title.toLowerCase())
    }
    
    // Also check for numbered sections like "1. Section Name" at start of lines
    const numberedSectionPattern = /^(\d+)\.\s+([A-Z][^\n]{3,60})$/gm
    let numMatch
    while ((numMatch = numberedSectionPattern.exec(markdown)) !== null) {
      const title = numMatch[2].trim()
      if (seenBoldTitles.has(title.toLowerCase())) continue
      
      const base = slugifyRawHeading(title)
      const count = slugCounts[base] ?? 0
      slugCounts[base] = count + 1
      const id = count > 0 ? `${base}-${count}` : base
      headings.push({ id, title, level: 2 })
      seenBoldTitles.add(title.toLowerCase())
    }
  }

  // NORMALIZE HEADING LEVELS:
  // If there's only one H1 (likely the title/query) followed by H2s, skip the lone H1
  // and treat H2s as top-level sections. Also, if the first H1 looks like a user query
  // (contains "?" or starts with common question words), filter it out from TOC.
  if (headings.length > 1) {
    const h1Headings = headings.filter(h => h.level === 1)
    const h2Headings = headings.filter(h => h.level === 2)
    
    // Check if first heading is a user query/title that should be excluded from TOC
    const firstHeading = headings[0]
    const isQueryLikeTitle = firstHeading && firstHeading.level === 1 && (
      firstHeading.title.includes('?') ||
      /^(what|why|how|when|where|who|which|can|should|will|is|are|does|do)\s/i.test(firstHeading.title) ||
      /["'""\*]/.test(firstHeading.title) || // Quoted or markdown-formatted titles
      /^["\*]/.test(firstHeading.title) || // Starts with quote or asterisk
      /–\s*(Intelligence|Assessment|Analysis|Report|Brief)/i.test(firstHeading.title) || // "Topic – Intelligence Assessment" pattern
      (firstHeading.title.length > 50 && firstHeading.title.includes(':')) // Long titles with colons are usually doc titles
    )
    
    // If we have 1 H1 that looks like a query/title and ANY H2s, filter out the H1
    // Relaxed from h2Headings.length >= 3 to >= 1 since even 1 section means we have content
    if (h1Headings.length === 1 && h2Headings.length >= 1 && isQueryLikeTitle) {
      // Remove the query-like H1 and normalize H2s to level 1
      const filteredHeadings = headings.filter(h => h !== firstHeading)
      const minLevel = Math.min(...filteredHeadings.map(h => h.level))
      return filteredHeadings.map(h => ({
        ...h,
        level: h.level - minLevel + 1
      }))
    }
    
    // Also filter if first H1 is just the document title and we have proper sections below
    // This handles cases where the title isn't query-like but is still a document heading, not a section
    if (h1Headings.length === 1 && h2Headings.length >= 2 && firstHeading === h1Headings[0]) {
      // Check if H2s look like actual content sections (Executive Summary, Situation Overview, etc.)
      const sectionKeywords = ['summary', 'overview', 'analysis', 'context', 'implications', 'recommendations', 'conclusion', 'assessment', 'findings', 'dynamics', 'dimensions', 'breakdown']
      const h2sLookLikeSections = h2Headings.some(h => 
        sectionKeywords.some(kw => h.title.toLowerCase().includes(kw))
      )
      
      if (h2sLookLikeSections) {
        const filteredHeadings = headings.filter(h => h !== firstHeading)
        const minLevel = Math.min(...filteredHeadings.map(h => h.level))
        return filteredHeadings.map(h => ({
          ...h,
          level: h.level - minLevel + 1
        }))
      }
    }
    
    // Otherwise, just normalize levels so minimum becomes 1
    const minLevel = Math.min(...headings.map(h => h.level))
    if (minLevel > 1) {
      return headings.map(h => ({
        ...h,
        level: h.level - minLevel + 1
      }))
    }
  }

  return headings
}

function slugifyRawHeading(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-') || 'section'
  )
}

function getPlainText(children: React.ReactNode | React.ReactNode[]): string {
  if (Array.isArray(children)) {
    return children.map(getPlainText).join(' ').trim()
  }
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (React.isValidElement(children)) {
    return getPlainText(children.props.children)
  }
  return ''
}

function generateTocItems(sections: ReportSection[]): { id: string; title: string; level: number }[] {
  const items: { id: string; title: string; level: number }[] = []
  
  function traverse(section: ReportSection, level: number) {
    items.push({ id: section.id, title: section.title, level })
    section.subsections?.forEach((sub: any) => traverse(sub, level + 1))
  }
  
  sections.forEach(s => traverse(s, 1))
  return items
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

export default WrittenReportTab



